// Environment Configuration (.env.example)
/*
# Firebase Configuration
REACT_APP_FIREBASE_API_KEY=your_api_key_here
REACT_APP_FIREBASE_AUTH_DOMAIN=your_project.firebaseapp.com
REACT_APP_FIREBASE_PROJECT_ID=your_project_id
REACT_APP_FIREBASE_STORAGE_BUCKET=your_project.appspot.com
REACT_APP_FIREBASE_MESSAGING_SENDER_ID=123456789
REACT_APP_FIREBASE_APP_ID=1:123456789:web:abcdef

# Application Configuration
REACT_APP_APP_NAME=Oodles Internships
REACT_APP_APP_VERSION=1.0.0
REACT_APP_ENVIRONMENT=development

# Email Configuration (for notifications)
REACT_APP_EMAIL_SERVICE_ID=your_emailjs_service_id
REACT_APP_EMAIL_TEMPLATE_ID=your_emailjs_template_id
REACT_APP_EMAIL_PUBLIC_KEY=your_emailjs_public_key

# Analytics
REACT_APP_GA_TRACKING_ID=UA-XXXXXXXXX-1

# Feature Flags
REACT_APP_ENABLE_NOTIFICATIONS=true
REACT_APP_ENABLE_FILE_UPLOAD=true
REACT_APP_ENABLE_CHAT=false
*/

import { initializeApp } from 'firebase/app';
import { getFirestore } from "firebase/firestore";
import { getAuth, setPersistence, browserLocalPersistence, updateProfile } from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  addDoc, 
  collection, 
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp
} from "firebase/firestore";

// Firebase Configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Validate Firebase configuration
const validateFirebaseConfig = () => {
  const requiredKeys = [
    'apiKey', 'authDomain', 'projectId', 
    'storageBucket', 'messagingSenderId', 'appId'
  ];
  
  const missingKeys = requiredKeys.filter(key => !firebaseConfig[key]);
  
  if (missingKeys.length > 0) {
    console.error(
      `Missing Firebase configuration keys: ${missingKeys.join(', ')}. ` +
      'Please check your .env file and ensure all required Firebase environment variables are set.'
    );
    // Don't throw in development to allow the app to continue running
    if (process.env.NODE_ENV === 'production') {
      throw new Error('Firebase configuration is incomplete');
    }
  }
};

// Initialize Firebase with error handling
let app;
let auth;
let db;

try {
  validateFirebaseConfig();
  
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  db = getFirestore(app);
  
  console.log('Firebase initialized successfully');
} catch (error) {
  console.error('Firebase initialization failed:', error);
  // In development, create mock objects to prevent app crashes
  if (process.env.NODE_ENV === 'development') {
    console.warn('Running in development mode with Firebase initialization errors');
  } else {
    throw error;
  }
}

// Helper functions for common operations
export const firestoreHelpers = {
  // Create document with validation
  async createDocument(collectionName, data, docId = null) {
    try {
      if (!db) throw new Error('Firestore not initialized');
      
      if (docId) {
        const docRef = doc(db, collectionName, docId);
        await setDoc(docRef, {
          ...data,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        return docId;
      } else {
        const docRef = await addDoc(collection(db, collectionName), {
          ...data,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        return docRef.id;
      }
    } catch (error) {
      console.error(`Error creating document in ${collectionName}:`, error);
      throw error;
    }
  },

  // Update document with timestamp
  async updateDocument(collectionName, docId, data) {
    try {
      if (!db) throw new Error('Firestore not initialized');
      
      const docRef = doc(db, collectionName, docId);
      await updateDoc(docRef, {
        ...data,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error(`Error updating document ${docId} in ${collectionName}:`, error);
      throw error;
    }
  },

  // Get document with error handling
  async getDocument(collectionName, docId) {
    try {
      if (!db) throw new Error('Firestore not initialized');
      
      const docRef = doc(db, collectionName, docId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error(`Document ${docId} not found in ${collectionName}`);
      }
    } catch (error) {
      console.error(`Error getting document ${docId} from ${collectionName}:`, error);
      throw error;
    }
  },

  // Query collection with error handling
  async queryCollection(collectionName, constraints = []) {
    try {
      if (!db) throw new Error('Firestore not initialized');
      
      const collectionRef = collection(db, collectionName);
      const q = query(collectionRef, ...constraints);
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error(`Error querying ${collectionName}:`, error);
      throw error;
    }
  },

  // Real-time listener with error handling
  subscribeToCollection(collectionName, constraints = [], callback, errorCallback) {
    try {
      if (!db) throw new Error('Firestore not initialized');
      
      const collectionRef = collection(db, collectionName);
      const q = query(collectionRef, ...constraints);
      
      return onSnapshot(q, 
        (querySnapshot) => {
          const data = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          callback(data);
        },
        (error) => {
          console.error(`Error in real-time listener for ${collectionName}:`, error);
          if (errorCallback) errorCallback(error);
        }
      );
    } catch (error) {
      console.error(`Error setting up listener for ${collectionName}:`, error);
      if (errorCallback) errorCallback(error);
    }
  }
};

export { 
  app, 
  auth, 
  db,
  doc, 
  addDoc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  collection, 
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  updateProfile
};